import { useCallback, useMemo, useRef, useState } from "react";

import { EmdrBoardAPI } from "../infrastructure/api/EmdrBoard.api";
import { createSerialQueue, useRelayPoll } from "./useRelayPoll";

export type RemoteBoardStatus = "idle" | "connecting" | "live" | "failed";

/** The board's side of the mailbox, as the BLS channel uses it. */
export type BoardRelay = {
    post: (payload: string) => Promise<void>;
    /** Messages from the patient's device. Returns the way to stop listening. */
    subscribe: (handler: (payload: string, sender: string) => void) => () => void;
    /** A patient opened the link (again). Returns the way to stop listening. */
    onArrival: (handler: () => void) => () => void;
};

export type RemoteBoard = {
    /** The mailbox, once the link exists; the BLS channel sends through it too. */
    relay: BoardRelay | null;
    status: RemoteBoardStatus;
    /** The link to send the patient. */
    shareUrl: string | null;
    /** Whether the patient's device is polling right now. */
    patientPresent: boolean;
    /** Get the link and start reading. A second call reuses both. */
    share: () => Promise<void>;
};

/**
 * The board's link for a patient who is not in the consulting room.
 *
 * Asked for only when the therapist wants to share — a board used face to
 * face never touches the network. From then on the board posts every message
 * to a mailbox on our server and reads the patient's every second: no media
 * server, nothing but HTTP.
 */
export function useBoardRelay(sessionId: string): RemoteBoard {
    const [status, setStatus] = useState<RemoteBoardStatus>("idle");
    const [shareUrl, setShareUrl] = useState<string | null>(null);
    const [cursor, setCursor] = useState<number | null>(null);
    const [patientPresent, setPatientPresent] = useState(false);
    const listeners = useRef(new Set<(payload: string, sender: string) => void>());
    const arrivals = useRef(new Set<() => void>());
    const pending = useRef<Promise<void> | null>(null);
    const wasPresent = useRef(false);

    const share = useCallback(async () => {
        if (cursor !== null) return;
        if (pending.current) return pending.current;
        setStatus("connecting");
        pending.current = (async () => {
            try {
                const { data } = await EmdrBoardAPI.link(sessionId);
                setShareUrl(data.share_url);
                setCursor(data.cursor);
                setStatus("live");
            } catch {
                setStatus("failed");
            } finally {
                pending.current = null;
            }
        })();
        return pending.current;
    }, [cursor, sessionId]);

    useRelayPoll({
        cursor,
        fetch: async (after) => (await EmdrBoardAPI.messages(sessionId, after)).data,
        onMessage: (payload, sender) => listeners.current.forEach((listener) => listener(payload, sender)),
        onPresence: (present) => {
            setPatientPresent(present);
            if (present && !wasPresent.current) arrivals.current.forEach((listener) => listener());
            wasPresent.current = present;
        },
    });

    const live = cursor !== null;
    // One message after another, in the order the board said them.
    const queue = useRef(createSerialQueue()).current;
    const relay = useMemo<BoardRelay | null>(
        () =>
            live
                ? {
                      post: (payload) =>
                          queue(async () => {
                              await EmdrBoardAPI.post(sessionId, payload);
                          }),
                      subscribe: (handler) => {
                          listeners.current.add(handler);
                          return () => listeners.current.delete(handler);
                      },
                      onArrival: (handler) => {
                          arrivals.current.add(handler);
                          return () => arrivals.current.delete(handler);
                      },
                  }
                : null,
        [live, queue, sessionId]
    );

    return { relay, status, shareUrl, patientPresent, share };
}
